import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, RotateCcw, ShieldCheck, MessageCircle, Sparkles } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import HomeNewsletter from "@/components/HomeNewsletter";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LangContext";
import { fetchProducts } from "@/lib/api";
import { STATIC_PRODUCTS } from "@/lib/products";

const Hero = () => {
  const { t, lang } = useLang();
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 pt-10 pb-10 md:pt-14 md:pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 flv-fade-up">
          <div className="eyebrow mb-5">{t.hero.eyebrow}</div>
          <h1
            data-testid="hero-title"
            className="font-serif-display text-5xl sm:text-6xl md:text-6xl lg:text-[4.6rem] leading-[0.98] tracking-tight whitespace-pre-line"
          >
            {t.hero.title}
          </h1>
          <p className="mt-6 text-[16px] md:text-[17px] text-[#4a4a4a] max-w-md leading-relaxed">
            {t.hero.subtitle}
          </p>
          <p
            data-testid="hero-summer-line"
            className="mt-3 italic text-[15px] text-[#8a6d3b] max-w-md"
          >
            {lang === "fr"
              ? "☀️ Froid en 3 secondes — partout où tu vas cet été"
              : "☀️ Cool in 3 seconds — wherever you go this summer"}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href="#collection"
              data-testid="hero-cta-button"
              className="group inline-flex items-center justify-center gap-3 bg-[#3D2B1F] text-white px-9 py-5 text-sm tracking-[0.22em] uppercase rounded-md shadow-[0_10px_30px_-15px_rgba(61,43,31,0.6)] hover:bg-[#1A1A1A] hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-20px_rgba(61,43,31,0.55)] transition-all duration-300"
            >
              {lang === "fr" ? "Découvrir la collection" : "Discover the collection"}
              <ArrowRight size={16} strokeWidth={1.6} className="transition-transform group-hover:translate-x-1" />
            </a>
            <Link
              to="/story"
              data-testid="hero-secondary-button"
              className="inline-flex items-center justify-center gap-3 px-6 py-4 text-xs tracking-[0.22em] uppercase underline underline-offset-[6px] decoration-[#1A1A1A]/30 hover:decoration-[#1A1A1A] transition-colors"
            >
              {t.hero.ctaSecondary}
            </Link>
          </div>
        </div>

        <div className="lg:col-span-6 flv-fade-up" style={{ animationDelay: "200ms" }}>
          <div className="relative">
            <div className="aspect-[5/4] md:aspect-[4/3] overflow-hidden bg-[#ebe7df] rounded-sm">
              <img
                src="/photos/hero-myflovea.webp"
                alt="myFLOVEA hero"
                width="1600"
                height="1200"
                fetchpriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Slogan strip */}
      <div data-testid="slogan-marquee" className="border-y border-[#dcd7ce] bg-[#ebe7df]/40 overflow-hidden">
        <div className="flex whitespace-nowrap flv-marquee py-4">
          {Array.from({ length: 2 }).map((_, blk) => (
            <div key={blk} className="flex shrink-0 items-center">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <span
                  key={`${blk}-${n}`}
                  className="font-serif-display italic text-xl md:text-2xl px-8 text-[#1A1A1A]"
                >
                  {t.footer.tagline}
                  <span className="px-5 text-[#4a4a4a]">·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AuthorityBand = () => {
  const { lang } = useLang();
  const intro = lang === "fr"
    ? "Basé sur les bienfaits reconnus de la thermothérapie et de la vibration mécanique"
    : "Backed by the recognized benefits of thermotherapy and mechanical vibration";
  const checks = lang === "fr"
    ? ["Certifié CE", "Normes européennes", "Livraison suivie"]
    : ["CE certified", "European standards", "Tracked delivery"];
  return (
    <section
      data-testid="authority-band"
      className="bg-[#ebe7df] border-y border-[#dcd7ce]"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <p className="text-[14px] md:text-[15px] text-[#3D2B1F] leading-relaxed max-w-2xl">
          {intro}
        </p>
        <ul className="flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-[#3D2B1F]">
          {checks.map((c, i) => (
            <li key={i} className="inline-flex items-center gap-2">
              <span className="text-[#C9A961] text-base leading-none">✓</span>
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const Reassurance = () => {
  const { t } = useLang();
  const items = [
    { Icon: Truck, ...t.reassurance.shipping, id: "shipping" },
    { Icon: RotateCcw, ...t.reassurance.returns, id: "returns" },
    { Icon: ShieldCheck, ...t.reassurance.payment, id: "payment" },
    { Icon: MessageCircle, ...t.reassurance.support, id: "support" },
  ];
  return (
    <section
      data-testid="reassurance-band"
      className="border-b border-[#dcd7ce]"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 py-14">
        {items.map(({ Icon, title, sub, id }) => (
          <div
            key={id}
            data-testid={`reassurance-${id}`}
            className="flex flex-col md:flex-row md:items-start gap-4 text-left"
          >
            <Icon size={26} strokeWidth={1.25} className="text-[#1A1A1A] shrink-0" />
            <div>
              <div className="text-sm font-medium tracking-wide">{title}</div>
              <div className="text-xs text-[#4a4a4a] mt-1.5 leading-relaxed">{sub}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const Collection = ({ products }) => {
  const { t } = useLang();
  return (
    <section
      id="collection"
      data-testid="products-section"
      className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 pt-4 md:pt-6 pb-20 md:pb-28"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-8 md:mb-10">
        <div className="md:col-span-5">
          <div className="eyebrow mb-3">{t.products.eyebrow}</div>
          <h2 className="font-serif-display text-3xl md:text-4xl leading-[1.05] tracking-tight">
            {t.products.title}
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 flex items-end">
          <p className="text-[15px] md:text-[16px] text-[#4a4a4a] leading-relaxed max-w-md">
            {t.products.sub}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
        {products.map((p, i) => (
          <ProductCard key={p.id} product={p} index={i} />
        ))}
      </div>
    </section>
  );
};

const Story = () => {
  const { t } = useLang();
  return (
    <section
      data-testid="story-section"
      className="bg-[#ebe7df]"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="aspect-[5/6] overflow-hidden bg-[#dcd7ce] rounded-sm">
            <img
              src="https://customer-assets.emergentagent.com/job_comfort-tech-shop/artifacts/9hkojqdy_ChatGPT%20Image%2029%20juin%202026%2C%2014_05_14.png"
              alt="myFLOVEA — Le confort qui te suit partout"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2 lg:pl-8">
          <div className="eyebrow mb-5">{t.story.eyebrow}</div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
            {t.story.title}
          </h2>
          <p className="mt-8 text-[17px] text-[#4a4a4a] leading-relaxed">
            {t.story.body1}
          </p>
          <p className="mt-5 text-[17px] text-[#4a4a4a] leading-relaxed">
            {t.story.body2}
          </p>
          <Link
            to="/story"
            data-testid="story-cta"
            className="inline-flex items-center gap-3 mt-10 text-xs tracking-[0.22em] uppercase underline underline-offset-[6px] decoration-[#1A1A1A]/30 hover:decoration-[#1A1A1A] transition-colors"
          >
            {t.story.cta}
            <ArrowRight size={14} strokeWidth={1.4} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const Guarantees = () => {
  const { t } = useLang();
  return (
    <section
      data-testid="guarantees-section"
      className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 py-24 md:py-32"
    >
      <div className="text-center max-w-2xl mx-auto mb-16">
        <div className="eyebrow mb-4">{t.guarantees.eyebrow}</div>
        <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
          {t.guarantees.title}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {t.guarantees.items.map((item, idx) => (
          <div
            key={idx}
            data-testid={`guarantee-${idx}`}
            className="border border-[#dcd7ce] bg-[#F5F0EB] p-10 hover:border-[#1A1A1A] transition-colors group rounded-sm"
          >
            <div className="font-serif-display text-2xl md:text-3xl leading-tight mb-4">
              0{idx + 1}
            </div>
            <div className="text-base font-medium tracking-wide mb-2">{item.title}</div>
            <p className="text-sm text-[#4a4a4a] leading-relaxed">{item.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const Home = () => {
  // Start from static catalogue so cards render INSTANTLY and never disappear,
  // even if /api/products is slow or temporarily unreachable.
  const [products, setProducts] = useState(STATIC_PRODUCTS);
  const { lang } = useLang();

  useEffect(() => {
    fetchProducts()
      .then((apiProducts) => {
        if (Array.isArray(apiProducts) && apiProducts.length > 0) {
          setProducts(apiProducts);
        }
      })
      .catch((err) => {
        // Keep the static fallback — no UI degradation for the user.
        console.warn("Falling back to static product catalogue", err);
      });
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        lang={lang}
        path="/"
        title={lang === "fr" ? "Bien-être tech & confort premium" : "Premium wellness tech & comfort"}
        description={
          lang === "fr"
            ? "Découvrez le Coussin Massant Chauffant myFLOVEA (à partir de 79€) — 9 modes, chaleur apaisante, expédié depuis l'Allemagne, livré en 3 à 7 jours ouvrés. Bien-être tech premium, satisfait ou remboursé 30 jours."
            : "Discover the myFLOVEA Heated Massage Cushion (from €79) — 9 modes, soothing heat, ships from Germany, delivered in 3–7 business days. Premium wellness tech, 30-day money back."
        }
      />
      <Hero />
      <AuthorityBand />
      <Collection products={products} />
      <Reassurance />
      <HomeNewsletter />
      <Story />
      <Guarantees />
    </div>
  );
};

export default Home;
